// `getOgImageUrl(surface, opts)` — maps a `ShareSurfaceInput` to its absolute
// OG image URL. Surfaces without a registered `buildPath` fall back to
// the brand-default OG so no surface ever 404s when shared.
//
// `opts.demoMode` appends `?demo-mode` to the OG URL. Devnet share-link
// builders set this so the OG route handler renders the [DEMO MODE] badge
// and fetches data from devnet (mirroring the marker on the share URL).

import { appendDemoModeFlag } from '@/lib/network';
import { resolveCanonicalOrigin } from '@/lib/seo/origin';
import { normalizeShareSurface, type ShareSurfaceInput } from '@/lib/share/events';

import { OG_SURFACE_REGISTRY, type OgSurfaceDescriptor } from './registry';

const DEFAULT_OG_PATH = '/api/og/default';

export interface GetOgImageUrlOpts {
  /** When true, appends `?demo-mode` to the resulting URL. Devnet-only. */
  demoMode?: boolean;
}

export function getOgImageUrl(input: ShareSurfaceInput, opts: GetOgImageUrlOpts = {}): string {
  const surface = normalizeShareSurface(input);
  // Mapped-type per-key descriptor is narrower than `OgSurfaceDescriptor<ShareSurface>`;
  // same pattern as `lib/og/metadata.ts`. Exhaustiveness is compile-time guaranteed.
  // ALLOW-UNKNOWN: see comment above
  const descriptor = OG_SURFACE_REGISTRY[surface.kind] as unknown as OgSurfaceDescriptor;
  const path = descriptor.buildPath(surface) ?? DEFAULT_OG_PATH;
  const url = new URL(path, resolveCanonicalOrigin());
  if (opts.demoMode === true) appendDemoModeFlag(url);
  return url.toString();
}
